import { useRouter } from '@abyss/web/hooks/useRouter';
import { config } from '@abyss/web/tools/config';
import { Router } from '@abyss/web/ui/Router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import GuestFooter from '../../common/GuestFooter/GuestFooter';
import GuestHeader from '../../common/GuestHeader/GuestHeader';
import { PortalData } from '../../common/Portal';
import { getLanguage } from '../ProviderSearch/context/Internationalization/helpers';
import { LANGUAGES } from '../ProviderSearch/context/Internationalization/types';
import {
  PSXBlueGreenCanaryGraphqlApi,
  PSXBlueGreenCanaryParcelUrl,
  populatePortalDataFromGuestPlanPicker,
} from './utils';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'abyss-parcel': any;
      'portal-data': any;
    }
  }
}

export const ProviderSearchForPortalContainer = () => {
  const { state } = useLocation();
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const flags = useFlags();

  const savedState =
    state || JSON.parse(sessionStorage.getItem('portalState') || '{}');
  const urlParams = new URLSearchParams(window.location.search);
  const lang =
    getLanguage(urlParams.get('lang') || '')?.locale || LANGUAGES.en.locale;
  const {
    lineOfBusiness,
    reciprocityId,
    population,
    coverageTypeCode,
    coverageEffStartDate,
    coverageEffEndDate,
    stateCode,
    signInUrl,
  } = savedState;

  useEffect(() => {
    if (state) {
      sessionStorage.setItem('portalState', JSON.stringify(state));
    }
  }, [state]);

  let homePath = '/';
  if (coverageTypeCode === 'B') {
    homePath = '/?chipValue=Behavioral%20Health&coverageType=B';
  } else if (coverageTypeCode === 'D') {
    homePath = '/?chipValue=Dental&coverageType=D';
  } else if (coverageTypeCode === 'V') {
    homePath = '/?chipValue=Vision&coverageType=V';
  }

  const portalData: PortalData = {
    portalName: 'guest-plan-selection',
    portalConfig: {
      basePath: '/guest-plan-selection/browse',
      headerChipsEnabled: coverageTypeCode === 'M',
      theme: 'uhc',
      language: lang,
      homePage: homePath,
      isGuestUser: true,
      showCost: false,
      showTreatmentCenter: true,
      includeBaseCss: true,
      showVirtualVisits: true,
      showOnlineScheduling: true,
      graphQlUrl: PSXBlueGreenCanaryGraphqlApi(),
      signInUrl,
    },
    portalMemberInfo: populatePortalDataFromGuestPlanPicker(
      lineOfBusiness,
      reciprocityId,
      population,
      coverageTypeCode,
      coverageEffStartDate,
      coverageEffEndDate,
      stateCode,
      state?.planName,
      state?.planYear
    ),
  };

  const parcelUrl = PSXBlueGreenCanaryParcelUrl(flags, searchParams);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = parcelUrl || config('PSX_PARCEL_URL') || '';
    document.head.appendChild(script);
  }, [parcelUrl]);

  return (
    <React.Fragment>
      <GuestHeader />
      <Router.MetaTags title="Find Care" />
      <abyss-parcel
        import="provider-search-for-portal"
        portal-data={JSON.stringify(portalData)}
      />
      <GuestFooter />
    </React.Fragment>
  );
};
